import { useCallback, useState } from 'react';
import { useToast } from 'contexts/ToastContext';
import { hasFormErrors, validateForm } from 'utils/formValidation';

/**
 * Inline field + form-level validation for create/edit screens.
 */
export default function useFormValidation() {
  const { showToast } = useToast();
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const validate = useCallback((schema, values) => {
    const errors = validateForm(schema, values);
    setFieldErrors(errors);
    setFormError('');
    setSubmitAttempted(true);
    return !hasFormErrors(errors);
  }, []);

  const setServerError = useCallback(
    (message, field, options = {}) => {
      const text = message || 'Something went wrong. Please try again.';
      if (field) {
        setFieldErrors((prev) => ({ ...prev, [field]: text }));
        return;
      }
      setFormError('');
      if (options.toast !== false) {
        showToast(text, { severity: options.severity || 'error', humanize: options.humanize });
      } else {
        setFormError(text);
      }
    },
    [showToast]
  );

  const clearErrors = useCallback(() => {
    setFieldErrors({});
    setFormError('');
    setSubmitAttempted(false);
  }, []);

  const clearFieldError = useCallback((field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setFormError('');
  }, []);

  const getFieldError = useCallback((field) => fieldErrors[field] || '', [fieldErrors]);

  const fieldErrorProps = useCallback(
    (field) => ({
      error: Boolean(fieldErrors[field]),
      helperText: fieldErrors[field] || undefined
    }),
    [fieldErrors]
  );

  return {
    fieldErrors,
    formError,
    submitAttempted,
    validate,
    setFieldErrors,
    setFormError,
    setServerError,
    clearErrors,
    clearFieldError,
    getFieldError,
    fieldErrorProps
  };
}
